import { useState } from 'react'
import {Copy, AlertTriangle, CheckCircle, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { copiarTexto } from '@/utils/clipboard'

// ─── Banco de óleos complementares ───────────────────────────────────────────

export interface OleoComplementar {
  id: string
  nome: string
  tamanho: string
  valor: number
  pv: number
  prioridades: string[]
  indicacoes: Record<string, string>
  alertas: string[]
  contraindicado_gestante: boolean
  contraindicado_epilepsia: boolean
  contraindicado_hipertensao: boolean
  protocolo: {
    comoUsar: string[]
    quandoUsar: string[]
    avisos?: string[]
  }
}

export const OLEOS_COMPLEMENTARES: OleoComplementar[] = [
  {
    id: 'vetiver',
    nome: 'Vetiver',
    tamanho: '5ml',
    valor: 162.50,
    pv: 18,
    prioridades: ['sono e descanso', 'ansiedade e estresse', 'foco e memória', 'medo e insegurança', 'estagnação e bloqueios'],
    indicacoes: {
      'sono e descanso': 'aterramento profundo que induz sono de qualidade',
      'ansiedade e estresse': 'acalma o sistema nervoso profundamente',
      'foco e memória': 'ancora a mente dispersa e melhora concentração',
      'medo e insegurança': 'traz sensação de segurança e estabilidade',
      'estagnação e bloqueios': 'reconecta com a força interior',
    },
    alertas: [],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: true,
    protocolo: {
      comoUsar: [
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar na sola dos pés, nuca e pulsos. É o método mais eficaz.',
        'Difusor: 2 a 3 gotas. Combina bem com Lavanda e Cedro.',
      ],
      quandoUsar: [
        'Dia: tópico na sola dos pés para foco e aterramento',
        'Noite: tópico na nuca e pulsos para sono profundo',
        'Difusor: qualquer período',
      ],
    },
  },
  {
    id: 'serenity',
    nome: 'Mix Calmante Noturno (Serenity)',
    tamanho: '15ml',
    valor: 255.00,
    pv: 34,
    prioridades: ['sono e descanso', 'ansiedade e estresse', 'equilíbrio emocional geral'],
    indicacoes: {
      'sono e descanso': 'blend específico para induzir sono profundo e relaxamento noturno',
      'ansiedade e estresse': 'acalma a mente acelerada antes de dormir',
      'equilíbrio emocional geral': 'restaura o equilíbrio do sistema nervoso',
    },
    alertas: [],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar por 1 a 2 minutos antes de dormir.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar no peito, nuca e sola dos pés antes de dormir.',
        'Difusor: 3 a 4 gotas 30 minutos antes de dormir.',
      ],
      quandoUsar: [
        'Noite: aromático e tópico 30 minutos antes de dormir',
        'Difusor: ligar 30 minutos antes de dormir',
      ],
    },
  },
  {
    id: 'bergamota',
    nome: 'Bergamota',
    tamanho: '5ml',
    valor: 92.50,
    pv: 12,
    prioridades: ['ansiedade e estresse', 'tristeza e apatia', 'autoestima e merecimento', 'equilíbrio emocional geral', 'saúde da pele'],
    indicacoes: {
      'ansiedade e estresse': 'reduz cortisol e acalma o sistema nervoso',
      'tristeza e apatia': 'eleva o humor de forma natural e suave',
      'autoestima e merecimento': 'dissolve vergonha e autocrítica',
      'saúde da pele': 'regula oleosidade e melhora a aparência da pele',
      'equilíbrio emocional geral': 'traz leveza e alegria',
    },
    alertas: [
      'FOTOSSENSÍVEL: não aplicar na pele antes do sol. Aguardar 12 horas.',
      'Não usar em gestantes sem orientação profissional',
    ],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar por 1 a 2 minutos. Forma mais eficaz para o efeito emocional.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar nos pulsos, peito e nuca.',
        'Difusor: 3 a 4 gotas.',
      ],
      quandoUsar: [
        'Manhã: aromático para elevar o humor e a energia',
        'Dia: difusor para ambiente positivo',
        'Noite: tópico nos pulsos evitando exposição ao sol',
      ],
      avisos: [
        'FOTOSSENSÍVEL: não aplicar na pele antes do sol. Aguardar 12 horas.',
      ],
    },
  },
  {
    id: 'cedro',
    nome: 'Cedro (Cedarwood)',
    tamanho: '15ml',
    valor: 105.00,
    pv: 14,
    prioridades: ['sono e descanso', 'foco e memória', 'ansiedade e estresse', 'equilíbrio emocional geral'],
    indicacoes: {
      'sono e descanso': 'estimula produção de melatonina natural',
      'foco e memória': 'acalma a mente agitada e melhora concentração',
      'ansiedade e estresse': 'calmante suave do sistema nervoso',
      'equilíbrio emocional geral': 'traz estabilidade e serenidade',
    },
    alertas: [],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar antes de dormir ou em momentos de agitação.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar na nuca, sola dos pés e pulsos. Para cabelo: adicionar 2 gotas ao shampoo.',
        'Difusor: 3 a 4 gotas. Combina bem com Lavanda, Vetiver e Serenity.',
      ],
      quandoUsar: [
        'Dia: difusor durante estudo ou trabalho para foco',
        'Noite: tópico na nuca e sola dos pés para sono',
      ],
    },
  },
  {
    id: 'adaptiv',
    nome: 'Mix de Adaptabilidade (Adaptiv)',
    tamanho: '15ml',
    valor: 283.75,
    pv: 37,
    prioridades: ['ansiedade e estresse', 'estagnação e bloqueios', 'energia e disposição', 'equilíbrio emocional geral'],
    indicacoes: {
      'ansiedade e estresse': 'blend específico para ansiedade crônica e adaptação a mudanças',
      'estagnação e bloqueios': 'dissolve resistência e rigidez emocional',
      'energia e disposição': 'restaura o equilíbrio após esgotamento',
      'equilíbrio emocional geral': 'apoia em momentos de transição',
    },
    alertas: [],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar em momentos de ansiedade ou estresse agudo.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar nos pulsos, nuca e peito.',
        'Difusor: 3 a 4 gotas durante situações de estresse.',
        'Pastilhas: 1 pastilha quando sentir ansiedade ou estresse agudo.',
      ],
      quandoUsar: [
        'Manhã: aromático para preparar para o dia',
        'Dia: tópico nos pulsos em momentos de estresse',
        'Noite: difusor para relaxamento',
      ],
    },
  },
  {
    id: 'ddr_prime',
    nome: 'Mix Proteção Celular (DDR Prime)',
    tamanho: '15ml',
    valor: 225.00,
    pv: 29,
    prioridades: ['imunidade e proteção', 'dores e inflamações', 'energia e disposição', 'equilíbrio emocional geral'],
    indicacoes: {
      'imunidade e proteção': 'proteção celular profunda e antioxidante',
      'dores e inflamações': 'reduz inflamação crônica no organismo',
      'energia e disposição': 'restaura a vitalidade celular',
      'equilíbrio emocional geral': 'reduz sensação de vulnerabilidade',
    },
    alertas: [
      'Fazer ciclos de 30 dias usando e 10 dias de pausa',
    ],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar na coluna vertebral e sola dos pés.',
        'Interno pastilhas: 1 pastilha 2 vezes ao dia após as refeições.',
        'Interno óleo: ingerir: pingue 1 gota em uma colher e tome com água após as refeições. Se preferir, use uma cápsula vegetal. Usar garrafa de vidro, nunca plástico.',
      ],
      quandoUsar: [
        'Manhã: 1 pastilha ou gota interna após o café da manhã',
        'Noite: 1 pastilha ou gota interna após o jantar',
        'Tópico: aplicar na coluna vertebral antes de dormir',
      ],
      avisos: [
        'Fazer ciclos de 30 dias usando e 10 dias de pausa.',
      ],
    },
  },
  {
    id: 'salvia_esclareia',
    nome: 'Sálvia-esclareia (Clary Sage)',
    tamanho: '5ml',
    valor: 122.50,
    pv: 17,
    prioridades: ['equilíbrio emocional geral', 'ansiedade e estresse', 'autoestima e merecimento'],
    indicacoes: {
      'equilíbrio emocional geral': 'equilíbrio hormonal que afeta diretamente o humor e emoções',
      'ansiedade e estresse': 'reduz ansiedade hormonal e irritabilidade',
      'autoestima e merecimento': 'restaura o equilíbrio feminino e a sensação de bem-estar',
    },
    alertas: [
      'Nunca usar durante a gravidez.',
    ],
    contraindicado_gestante: true,
    contraindicado_epilepsia: true,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar por 1 minuto para equilíbrio emocional hormonal.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar no abdômen inferior, nuca, pulsos e tornozelos.',
        'Difusor: 3 a 4 gotas.',
      ],
      quandoUsar: [
        'Manhã: aromático para equilíbrio emocional hormonal',
        'Dia: tópico no abdômen e pulsos',
        'Noite: tópico na nuca e tornozelos',
      ],
    },
  },
  {
    id: 'geranio',
    nome: 'Gerânio (Geranium)',
    tamanho: '5ml',
    valor: 93.75,
    pv: 12,
    prioridades: ['saúde da pele', 'equilíbrio emocional geral', 'ansiedade e estresse', 'autoestima e merecimento'],
    indicacoes: {
      'saúde da pele': 'regula oleosidade, melhora acne e rejuvenesce a pele',
      'equilíbrio emocional geral': 'estabiliza humor e emoções',
      'ansiedade e estresse': 'acalma ansiedade de origem hormonal',
      'autoestima e merecimento': 'fortalece confiança e expressão',
    },
    alertas: [],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar para equilíbrio emocional.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar nos pulsos, abdômen e nuca. Para pele: adicionar 1 gota ao hidratante facial.',
        'Difusor: 3 a 4 gotas.',
      ],
      quandoUsar: [
        'Manhã: aromático para equilíbrio emocional',
        'Dia: tópico nos pulsos',
        'Noite: tópico no abdômen e hidratante facial',
      ],
    },
  },
  {
    id: 'elevation',
    nome: 'Mix Alegria (Elevation)',
    tamanho: '15ml',
    valor: 348.50,
    pv: 42,
    prioridades: ['tristeza e apatia', 'energia e disposição', 'autoestima e merecimento', 'estagnação e bloqueios', 'equilíbrio emocional geral'],
    indicacoes: {
      'tristeza e apatia': 'eleva o humor e restaura a alegria de viver',
      'energia e disposição': 'estimula motivação e disposição',
      'autoestima e merecimento': 'fortalece a autoconfiança',
      'estagnação e bloqueios': 'desperta criatividade e movimento',
      'equilíbrio emocional geral': 'blend específico para elevar o estado emocional',
    },
    alertas: [
      'Uso preferencialmente diurno',
      'Não usar próximo à hora de dormir',
      'Fotossensível: não aplicar na pele antes do sol',
    ],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Aromático: 1 gota nas mãos, esfregar e inalar pela manhã ou em momentos de tristeza e baixa energia.',
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar nos pulsos, peito e nuca.',
        'Difusor: 3 a 4 gotas durante o dia para ambiente energizante.',
      ],
      quandoUsar: [
        'Manhã: aromático ao acordar para elevar o humor',
        'Dia: difusor no ambiente de trabalho ou estudo',
      ],
      avisos: [
        'Uso preferencialmente diurno. Não usar próximo à hora de dormir. Fotossensível: não aplicar na pele antes do sol.',
      ],
    },
  },
  {
    id: 'intune',
    nome: 'Roll-on Foco (InTune)',
    tamanho: 'Roll-on 10ml',
    valor: 252.50,
    pv: 32,
    prioridades: ['foco e memória', 'energia e disposição', 'estagnação e bloqueios'],
    indicacoes: {
      'foco e memória': 'blend específico para concentração e clareza mental',
      'energia e disposição': 'combate névoa mental e cansaço cognitivo',
      'estagnação e bloqueios': 'desperta a mente e supera procrastinação',
    },
    alertas: [
      'Sempre diluir em 5 a 10 gotas de óleo de coco fracionado antes de aplicar',
      'Evitar exposição ao sol por pelo menos 12 horas após aplicação',
    ],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Tópico: diluir em 5 a 10 gotas de óleo de coco fracionado antes de aplicar. Aplicar nas têmporas, nuca e pulsos antes de estudar ou trabalhar.',
        'Aromático: aplicar nos pulsos diluído e inalar diretamente.',
      ],
      quandoUsar: [
        'Manhã: aplicar antes de começar o trabalho ou estudo',
        'Dia: reaplicar a cada 2 a 3 horas conforme necessário',
      ],
      avisos: [
        'Sempre diluir antes de aplicar. Evitar exposição ao sol por pelo menos 12 horas após aplicação.',
      ],
    },
  },
  {
    id: 'zendocrine',
    nome: 'Mix Depurativo (Zendocrine)',
    tamanho: '5ml',
    valor: 73.75,
    pv: 9,
    prioridades: ['dores e inflamações', 'digestão e intestino', 'imunidade e proteção', 'peso e metabolismo', 'saúde da pele'],
    indicacoes: {
      'dores e inflamações': 'desintoxica o organismo reduzindo inflamação crônica',
      'digestão e intestino': 'apoia a digestão e eliminação de toxinas',
      'imunidade e proteção': 'limpa o organismo fortalecendo a imunidade',
      'peso e metabolismo': 'apoia a eliminação de toxinas que afetam o metabolismo',
      'saúde da pele': 'elimina toxinas que causam problemas de pele',
    },
    alertas: [
      'Fazer ciclos: 30 dias usando, 10 dias de pausa',
    ],
    contraindicado_gestante: true,
    contraindicado_epilepsia: false,
    contraindicado_hipertensao: false,
    protocolo: {
      comoUsar: [
        'Tópico: diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar sobre o fígado (lado direito do abdômen) e sola dos pés.',
        'Interno: ingerir: pingue 1 gota em uma colher e tome com água após as refeições. Se preferir, use uma cápsula vegetal. Alternativamente, 1 pastilha 2 a 3 vezes ao dia após as refeições. Usar garrafa de vidro para o óleo, nunca plástico.',
        'Difusor: 3 a 4 gotas para purificação do ambiente.',
      ],
      quandoUsar: [
        'Manhã: ingerir 1 gota em uma colher com água ou 1 pastilha após o café da manhã',
        'Noite: ingerir 1 gota em uma colher com água ou 1 pastilha após o jantar',
        'Tópico: aplicar sobre o fígado antes de dormir',
      ],
      avisos: [
        'Fazer ciclos: 30 dias usando, 10 dias de pausa.',
      ],
    },
  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizarPrioridade(p: string): string {
  return p.toLowerCase().trim()
}

function gerarTextoProtocolo(
  lead: any,
  oleosComprados: OleoComplementar[]
): string {
  const nome = lead?.nome?.split(' ')[0] || 'cliente'
  let texto = `*Seção 7 — Protocolo de Uso dos Óleos Complementares — ${nome}*\n\n`
  texto += `Protocolo de uso personalizado para os óleos que você adquiriu:\n\n`

  oleosComprados.forEach((oleo) => {
    texto += `━━━━━━━━━━━━━━━━━━━━━\n`
    texto += `*${oleo.nome}* (${oleo.tamanho})\n\n`
    texto += `*Como usar:*\n`
    oleo.protocolo.comoUsar.forEach((linha) => {
      texto += `→ ${linha}\n`
    })
    texto += `\n*Quando usar:*\n`
    oleo.protocolo.quandoUsar.forEach((linha) => {
      texto += `→ ${linha}\n`
    })
    if (oleo.protocolo.avisos && oleo.protocolo.avisos.length > 0) {
      texto += `\n`
      oleo.protocolo.avisos.forEach((aviso) => {
        texto += `⚠️ ${aviso}\n`
      })
    }
    texto += `\n`
  })

  texto += `━━━━━━━━━━━━━━━━━━━━━\n`
  texto += `⚠️ Este protocolo é de suporte complementar e não substitui orientação de um profissional de saúde. Em caso de condições de saúde específicas, consulte sempre um médico ou aromaterapeuta qualificado antes de iniciar qualquer protocolo.`
  return texto
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface Props {
  lead: any
}

export default function OutrasOpcoes({ lead }: Props) {
  const [copiadoLista, setCopiadoLista] = useState(false)
  const [copiadoProtocolo, setCopiadoProtocolo] = useState(false)
  const [oleosComprados, setOleosComprados] = useState<Set<string>>(new Set())

  // Prioridades do lead (normalizado)
  const prioridades: string[] = (
    lead?.prioridades ||
    lead?.prioridades_saude ||
    []
  ).map(normalizarPrioridade)

  // Condições de saúde (normalizado)
  const condicoes: string[] = (lead?.condicoes_saude || []).map((c: string) =>
    c.toLowerCase().trim()
  )

  const isGestante =
    condicoes.includes('gestante') || condicoes.includes('amamentando')
  const isEpilepsia = condicoes.includes('epilepsia')
  const isHipertensao =
    condicoes.includes('hipertensa(o)') || condicoes.includes('hipertensão')
  const isAutoimune = condicoes.includes('doença autoimune')
  const isMenuopausa =
    condicoes.includes('menopausa') || condicoes.includes('endometriose')

  // Se gestante → não exibir nada além do alerta
  if (isGestante) {
    return (
      <div
        className="rounded-xl p-5 border"
        style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="mt-0.5 shrink-0" style={{ color: '#DC2626' }} />
          <div>
            <p className="font-semibold text-sm" style={{ color: '#991B1B' }}>
              Cliente gestante: protocolo restrito.
            </p>
            <p className="text-sm mt-1" style={{ color: '#B91C1C' }}>
              Consulte sempre um profissional de saúde antes de indicar qualquer óleo essencial durante a gravidez.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Sem prioridades → não há como filtrar
  if (prioridades.length === 0) return null

  // Filtrar óleos que se aplicam às prioridades
  let oleosFiltrados = OLEOS_COMPLEMENTARES.filter((oleo) =>
    oleo.prioridades.some((p) => prioridades.includes(normalizarPrioridade(p)))
  )

  // Aplicar contraindicações
  if (isEpilepsia) {
    oleosFiltrados = oleosFiltrados.filter((o) => !o.contraindicado_epilepsia)
  }
  if (isHipertensao) {
    oleosFiltrados = oleosFiltrados.filter((o) => !o.contraindicado_hipertensao)
  }

  // Priorizar óleos para condições especiais (reordenar ao topo)
  if (isAutoimune) {
    const prioritarios = ['ddr_prime', 'zendocrine']
    oleosFiltrados.sort((a, b) => {
      const aP = prioritarios.includes(a.id) ? 0 : 1
      const bP = prioritarios.includes(b.id) ? 0 : 1
      return aP - bP
    })
  }
  if (isMenuopausa) {
    const prioritarios = ['salvia_esclareia', 'geranio']
    oleosFiltrados.sort((a, b) => {
      const aP = prioritarios.includes(a.id) ? 0 : 1
      const bP = prioritarios.includes(b.id) ? 0 : 1
      return aP - bP
    })
  }

  // Ocultar seção se não houver nenhum óleo
  if (oleosFiltrados.length === 0) return null

  // Gerar texto da lista de óleos no formato WhatsApp
  const textoLista = oleosFiltrados.map((oleo) => {
    const desconfortos = oleo.prioridades
      .map(normalizarPrioridade)
      .filter((p) => prioridades.includes(p))
      .join(', ')
    return `*${oleo.nome}* ${desconfortos}.`
  }).join('\n\n')

  const idsOleosComprados = oleosFiltrados.filter((o) => oleosComprados.has(o.id))
  const textoProtocolo = gerarTextoProtocolo(lead, idsOleosComprados)

  const toggleOleoComprado = (id: string) => {
    setOleosComprados((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const copiarLista = async () => {
    try {
      await copiarTexto(textoLista)
      setCopiadoLista(true)
      setTimeout(() => setCopiadoLista(false), 2500)
    } catch {
      /* fallback interno do helper já tentou execCommand */
    }
  }

  const copiarProtocolo = async () => {
    try {
      await copiarTexto(textoProtocolo)
      setCopiadoProtocolo(true)
      setTimeout(() => setCopiadoProtocolo(false), 2500)
    } catch {
      /* fallback interno do helper já tentou execCommand */
    }
  }

  const abrirWhatsAppProtocolo = () => {
    const numero = (lead?.whatsapp || '').replace(/\D/g, '')
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(textoProtocolo)}`
    window.open(url, '_blank')
  }

  return (
    <div className="space-y-5">

      {/* Campo de texto corrido — Seção 6 */}
      <div
        className="rounded-xl border p-5 space-y-4"
        style={{ borderColor: '#EDE0D4', backgroundColor: '#FFFFFF' }}
      >
        {/* Texto linha a linha com negrito nos nomes */}
        <div className="space-y-0">
          {oleosFiltrados.map((oleo, idx) => {
            const desconfortos = oleo.prioridades
              .map(normalizarPrioridade)
              .filter((p) => prioridades.includes(p))
              .join(', ')
            return (
              <div key={oleo.id}>
                <p className="text-sm leading-relaxed" style={{ color: '#2C2C2A' }}>
                  <strong>{oleo.nome}</strong> {desconfortos}.
                </p>
                {idx < oleosFiltrados.length - 1 && <div className="h-3" />}
              </div>
            )
          })}
        </div>

        {/* Botão copiar */}
        <button
          onClick={copiarLista}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all"
          style={{
            backgroundColor: copiadoLista ? '#D8F3DC' : '#FFFFFF',
            color: '#2D6A4F',
            border: '2px solid #52B788',
          }}
        >
          {copiadoLista ? <CheckCircle size={16} /> : <Copy size={16} />}
          {copiadoLista ? 'Copiado!' : 'Copiar'}
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 7 — Protocolo de Uso das Outras Opções                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="pt-4">
        <div className="py-4">
          <hr style={{ borderColor: '#EDE0D4' }} />
        </div>

        {/* Título Seção 7 */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1.5 h-8 rounded-full" style={{ backgroundColor: '#52B788' }} />
          <h3 className="text-xl font-semibold" style={{ color: '#2D6A4F', fontFamily: 'Cormorant Garamond, serif' }}>
            Seção 7 — Protocolo de Uso de Outras Opções de Óleos
          </h3>
        </div>

        {/* Instrução de uso */}
        <div
          className="rounded-xl p-4 border mb-5 flex items-start gap-3"
          style={{ borderColor: '#EDE0D4', backgroundColor: '#F8F4EF' }}
        >
          <CheckCircle size={18} className="shrink-0 mt-0.5" style={{ color: '#52B788' }} />
          <p className="text-sm" style={{ color: '#5F5E5A' }}>
            Marque abaixo quais óleos a cliente comprou. O Sistema Descubra seus Óleos gera o protocolo de uso apenas com os óleos selecionados.
          </p>
        </div>

        {/* Checkboxes por óleo */}
        <div className="space-y-3 mb-5">
          {oleosFiltrados.map((oleo) => {
            const checked = oleosComprados.has(oleo.id)
            return (
              <label
                key={oleo.id}
                className="flex items-center gap-3 cursor-pointer rounded-xl border p-4 transition-all"
                style={{
                  borderColor: checked ? '#52B788' : '#EDE0D4',
                  backgroundColor: checked ? '#F0FDF4' : '#FFFFFF',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOleoComprado(oleo.id)}
                  className="w-4 h-4 rounded accent-green-700"
                  style={{ accentColor: '#2D6A4F' }}
                />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm" style={{ color: '#2C2C2A' }}>
                    {oleo.nome}
                  </span>
                  <span className="ml-2 text-xs" style={{ color: '#5F5E5A' }}>
                    {oleo.tamanho}
                  </span>
                </div>
                {checked && (
                  <CheckCircle size={16} style={{ color: '#2D6A4F', flexShrink: 0 }} />
                )}
              </label>
            )
          })}
        </div>

        {/* Protocolo gerado */}
        {idsOleosComprados.length > 0 ? (
          <div className="space-y-4">
            {/* Cards de protocolo por óleo selecionado */}
            {idsOleosComprados.map((oleo) => (
              <div
                key={oleo.id}
                className="rounded-xl border p-5 space-y-4"
                style={{ borderColor: '#52B788', backgroundColor: '#FAFFFE' }}
              >
                {/* Cabeçalho */}
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: '#52B788' }} />
                  <div>
                    <h4 className="font-semibold text-base" style={{ color: '#2D6A4F', fontFamily: 'Cormorant Garamond, serif' }}>
                      {oleo.nome}
                    </h4>
                    <span className="text-xs" style={{ color: '#5F5E5A' }}>{oleo.tamanho}</span>
                  </div>
                </div>

                {/* Como usar */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#B08968' }}>
                    Como usar:
                  </p>
                  <ul className="space-y-2">
                    {oleo.protocolo.comoUsar.map((linha, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#2C2C2A' }}>
                        <span className="mt-0.5 shrink-0 font-bold" style={{ color: '#52B788' }}>→</span>
                        <span>{linha}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Quando usar */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#B08968' }}>
                    Quando usar:
                  </p>
                  <ul className="space-y-2">
                    {oleo.protocolo.quandoUsar.map((linha, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#2C2C2A' }}>
                        <span className="mt-0.5 shrink-0 font-bold" style={{ color: '#52B788' }}>→</span>
                        <span>{linha}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Avisos específicos do protocolo */}
                {oleo.protocolo.avisos && oleo.protocolo.avisos.length > 0 && (
                  <div
                    className="rounded-lg p-3 space-y-1"
                    style={{ backgroundColor: '#FFFBEB', borderLeft: '3px solid #FDE68A' }}
                  >
                    {oleo.protocolo.avisos.map((aviso, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs" style={{ color: '#92400E' }}>
                        <span className="shrink-0">⚠️</span>
                        <span>{aviso}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Botões de protocolo */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={copiarProtocolo}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all"
                style={{
                  backgroundColor: copiadoProtocolo ? '#D8F3DC' : '#FFFFFF',
                  color: '#2D6A4F',
                  border: '2px solid #52B788',
                }}
              >
                {copiadoProtocolo ? <CheckCircle size={16} /> : <Copy size={16} />}
                {copiadoProtocolo ? 'Copiado!' : 'Copiar protocolo'}
              </button>
              <button
                onClick={abrirWhatsAppProtocolo}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-white transition-all"
                style={{ backgroundColor: '#25D366' }}
              >
                <MessageCircle size={16} />
                Abrir no WhatsApp
              </button>
            </div>
          </div>
        ) : (
          <div
            className="rounded-xl border p-5 text-center"
            style={{ borderColor: '#EDE0D4', backgroundColor: '#F8F4EF' }}
          >
            <p className="text-sm" style={{ color: '#5F5E5A' }}>
              Selecione os óleos que a cliente comprou para gerar o protocolo de uso.
            </p>
          </div>
        )}
      </div>

    </div>
  )
}
